/**
 * 🎲 v5.54 (daily runs): every roll in this module — and every direct
 * roll in the GENERATION path (mapgen/missiongen/bossSkills/…) — routes
 * through `rand()`, which is Math.random unless a seeded source is
 * installed. startNewRun installs one for a daily and removes it in a
 * finally, so the same seed deals the same map/draft to every player;
 * live play (spawns, dice) always rolls under the null source.
 */
let source: (() => number) | null = null
export function setRandomSource(fn: (() => number) | null): void {
  source = fn
}
/** Swap the source and get the previous one back — the nest-safe form
 * for scoped streams (v5.57 withRunStream): an inner stream restores
 * the OUTER stream on exit instead of clearing it to live dice. */
export function swapRandomSource(fn: (() => number) | null): (() => number) | null {
  const prev = source
  source = fn
  return prev
}
export function rand(): number {
  return (source ?? Math.random)()
}

/** Returns a new array with the items shuffled (Fisher–Yates). */
export function shuffle<T>(items: readonly T[]): T[] {
  const result = [...items]
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1))
    ;[result[i], result[j]] = [result[j]!, result[i]!]
  }
  return result
}

/** Picks up to `count` distinct random items. */
export function sample<T>(items: readonly T[], count: number): T[] {
  return shuffle(items).slice(0, count)
}

/** Random integer in [min, max], inclusive. */
export function randInt(min: number, max: number): number {
  return min + Math.floor(rand() * (max - min + 1))
}

/**
 * Deterministic PRNG (mulberry32) — the same seed always replays the
 * same sequence. Used by the 🌟 shop (v2.75), whose stock is derived
 * from the current hour so it is identical across reloads and devices
 * and cannot be re-rolled by refreshing.
 */
export function seededRandom(seed: number): () => number {
  let state = seed >>> 0
  return () => {
    state = (state + 0x6D2B79F5) >>> 0
    let t = Math.imul(state ^ (state >>> 15), 1 | state)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

/** Picks one item according to relative weights. */
export function weightedPick<T>(
  entries: ReadonlyArray<readonly [T, number]>,
  rng: () => number = rand,
): T {
  const total = entries.reduce((sum, [, weight]) => sum + weight, 0)
  let roll = rng() * total
  for (const [item, weight] of entries) {
    roll -= weight
    if (roll <= 0) return item
  }
  return entries[entries.length - 1]![0]
}

let idCounter = 0

/** Unique-enough id for game entities within a run. */
export function makeId(prefix: string): string {
  idCounter = (idCounter + 1) % 1000
  return `${prefix}-${Date.now().toString(36)}${idCounter}-${Math.random().toString(36).slice(2, 6)}`
}
